import { TimeSeries, USGSMonitoringStationDevice } from '../types/USGSMONITORINGTYPE';

///////////////////////////////////////////////////
// USGS Data Utilities
///////////////////////////////////////////////////

/**
 * [Find a USGS TimeSeries by parameter_name and return { parameter, values } where parameter is the matched
 * TimeSeries object (or null) and values is an array of datapoint objects (newest-first)
 * containing { ts, value, qualifier, approval_status, did, uid }]
 *
 * @param   {[USGSMonitoringStationDevice]}  device         [The USGS device to pull data from]
 * @param   {[string]}  parameterName  [The parameter name to pull values for, e.g. "Gage height" or "Discharge"]
 * [Current available parameters: Gage height, Discharge, Water temperature]
 *
 * @return  {[<TimeSeries, values>]}
 * [TimeSeries is the matched TimeSeries object from the device's time_series array, or null if no match found.]
 *
 * [values is an array of objects with the following properties:
 * ts (timestamp),
 * value (the value of the parameter at that timestamp),
 * qualifier,
 * approval_status,
 * did,
 * uid]
 */
export function usgsGetAllValuesForParam(
  usgsDevice: USGSMonitoringStationDevice,
  parameterName: string,
): {
  parameter: TimeSeries | null;
  values: {
    ts: number;
    value: number | null;
    qualifier: string[] | null;
    approval_status: 'Provisional' | 'Approved' | null;
    did: number;
    uid: number;
  }[];
} {
  const paramNameNormalized = normalizeString(parameterName);

  const timeSeries = usgsDevice.time_series?.find((t) => {
    // compare normalized parameter_name;
    const nameNorm = normalizeString(t.parameter_name);
    return nameNorm === paramNameNormalized || false;
  });

  if (!timeSeries) return { parameter: null, values: [] };
  const timeSeriesID = timeSeries.id;
  const valuesForParam = (usgsDevice.data || [])
    .map((dataPoint) => {
      const point = getPointFromDataPoint(dataPoint as any, timeSeriesID);
      return {
        ts: dataPoint.ts,
        value: normalizeNumericValue(point?.value ?? null),
        qualifier: point?.qualifier ?? null,
        approval_status: point?.approval_status ?? null,
        did: dataPoint.did,
        uid: dataPoint.uid,
      };
    })
    .sort((a, b) => (b.ts ?? 0) - (a.ts ?? 0)); // sort newest-first by timestamp

  return { parameter: timeSeries, values: valuesForParam };
}

export function usgsGetMostRecentValueForParam(
  usgsDevice: USGSMonitoringStationDevice,
  parameterName: string,
): { parameter: TimeSeries | null; value: number | null } {
  const { parameter, values } = usgsGetAllValuesForParam(usgsDevice, parameterName);
  const mostRecentValue = values.length > 0 ? values[0].value : null;
  return { parameter, value: mostRecentValue };
}

/**
 * Get the most recent datapoint value for a single parameter on the device.
 *
 * @param {[USGSMonitoringStationDevice]} usgsDevice - USGS device to read from
 * @param {[String]} parameterName - parameter lookup (name, code, or id)
 * @returns an object with the matched `TimeSeries` (or `null`) and the
 * most-recent `value` (or `null` when unavailable)
 */

export function usgsGetMostRecentValues(usgsDevice: USGSMonitoringStationDevice): {
  [timeSeriesId: string]: {
    value: number | null;
    parameterName: string;
    unit_of_measure: string | null;
    ts: number | null;
    qualifier: string[] | null;
    approval_status: 'Provisional' | 'Approved' | null;
  };
} {
  // Build most-recent values directly from the first datapoint in the device's `data` array
  const mostRecentValues: {
    [timeSeriesId: string]: {
      value: number | null;
      parameterName: string;
      unit_of_measure: string | null;
      ts: number | null;
      qualifier: string[] | null;
      approval_status: 'Provisional' | 'Approved' | null;
    };
  } = {};

  const firstDataPoint = Array.isArray(usgsDevice?.data)
    ? (usgsDevice.data[0] ?? null) // Array — take first element
    : (usgsDevice?.data ?? null); // Single object or missing
  const ts = firstDataPoint?.ts ?? null;

  const deviceTimeSeries = usgsDevice.time_series || [];
  deviceTimeSeries.forEach((timeSeries) => {
    const point = getPointFromDataPoint(firstDataPoint as any, timeSeries.id);
    mostRecentValues[timeSeries.id] = {
      value: normalizeNumericValue(point?.value ?? null),
      parameterName: timeSeries.parameter_name,
      unit_of_measure: timeSeries.unit_of_measure ?? null,
      ts,
      qualifier: point?.qualifier ?? null,
      approval_status: point?.approval_status ?? null,
    };
  });

  return mostRecentValues;
}

/**
 * Get the most-recent value for every available parameter on the device.
 *
 * @param usgsDevice - USGS device to read from
 * @returns a dictionary mapping parameter display names to their most-recent value
 * (e.g. { "Water Temperature": 9.6, "Gage Height": 3.32 })
 */

/**
 * [Simple utility to get an array of all parameter names available for a given USGS device, based on its time_series array.]
 *
 * @param   {USGSMonitoringStationDevice}  usgsDevice  [usgsDevice to find available parameters for]
 *
 * @return  {[string]}                                   [array of parameter names]
 */
export function usgsGetAllParametersForDevice(usgsDevice: USGSMonitoringStationDevice) {
  const parameters = usgsDevice.time_series?.map((timeSeries) => timeSeries.parameter_name) || [];
  return parameters;
}

/**
 * Return each TimeSeries object with its datapoint values (values sorted newest-first).
 *
 * @param usgsDevice - device to read from
 * @returns array of { parameter: TimeSeries, values: [...] }
 */
export function usgsGetAllTimeSeriesWithValues(usgsDevice: USGSMonitoringStationDevice): Array<{
  parameter: TimeSeries;
  values: {
    ts: number;
    value: number | null;
    qualifier: string[] | null;
    approval_status: 'Provisional' | 'Approved' | null;
    did: number;
    uid: number;
  }[];
}> {
  const deviceTimeSeries = usgsDevice.time_series || [];
  return deviceTimeSeries.map((timeSeries) => {
    const timeSeriesID = timeSeries.id;
    const values = (usgsDevice.data || [])
      .map((dataPoint) => {
        const point = getPointFromDataPoint(dataPoint as any, timeSeriesID);
        return {
          ts: dataPoint.ts,
          value: normalizeNumericValue(point?.value ?? null),
          qualifier: point?.qualifier ?? null,
          approval_status: point?.approval_status ?? null,
          did: dataPoint.did,
          uid: dataPoint.uid,
        };
      })
      .sort((a, b) => (b.ts ?? 0) - (a.ts ?? 0)); // sort newest-first by timestamp

    return { parameter: timeSeries, values };
  });
}

/**
 * Get all datapoint values for a given timeSeries id.
 *
 * @param usgsDevice - USGS device to read from
 * @param timeSeriesId - the TimeSeries `id` key to lookup in each data point's `values` map
 * @returns array of datapoint objects: { ts, value, qualifier, approval_status, did, uid }
 */
export function usgsGetAllValuesForTimeSeriesId(
  usgsDevice: USGSMonitoringStationDevice,
  timeSeriesId: string,
): {
  ts: number;
  value: number | null;
  qualifier: string[] | null;
  approval_status: 'Provisional' | 'Approved' | null;
  did: number;
  uid: number;
}[] {
  if (!timeSeriesId) return [];

  const values = (usgsDevice.data || []).map((dataPoint) => {
    const point = getPointFromDataPoint(dataPoint as any, timeSeriesId);
    return {
      ts: dataPoint.ts,
      value: normalizeNumericValue(point?.value ?? null),
      qualifier: point?.qualifier ?? null,
      approval_status: point?.approval_status ?? null,
      did: dataPoint.did,
      uid: dataPoint.uid,
    };
  });

  // return sorted newest -> oldest by timestamp
  return values.sort((a, b) => (b.ts ?? 0) - (a.ts ?? 0));
}

// Normalizer: lowercase, remove non-alphanumerics, collapse whitespace
function normalizeString(name?: string) {
  return (name || '')
    .toString()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');
}

// Helper: support both shapes where the point is stored under `dataPoint.values[timeSeriesId]`
// or directly as `dataPoint[timeSeriesId]`
function getPointFromDataPoint(dataPoint: any, timeSeriesId: string) {
  if (!dataPoint) return null;
  return dataPoint.values?.[timeSeriesId] ?? dataPoint[timeSeriesId] ?? null;
}

// Ensure numeric values are returned as numbers when possible, otherwise null
function normalizeNumericValue(v: any): number | null {
  if (v == null) return null;
  if (typeof v === 'number') return v;
  // allow numeric strings
  const parsed = Number(v);
  return Number.isFinite(parsed) ? parsed : null;
}
